import { useEffect, useState } from "react";
import { api, formatErr, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Plus, Trash2, Download, FileCheck2, Mail, MessageCircle } from "lucide-react";

const EMPTY_ITEM = { description: "", hsn: "", qty: 1, unit: "Nos", rate: 0, gst_rate: 18 };

export default function AdminBilling({ resource, title }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    customer: { name: "", address: "", gstin: "", state: "", state_code: "", phone: "", email: "" },
    items: [{ ...EMPTY_ITEM }],
    is_igst: false, terms: "", notes: "",
  });

  const load = () => api.get(`/${resource}`).then(r => setItems(r.data));
  useEffect(() => { load(); }, [resource]);

  const [emailTarget, setEmailTarget] = useState(null);
  const [emailForm, setEmailForm] = useState({ to: "", subject: "", message: "" });
  const [emailBusy, setEmailBusy] = useState(false);

  const openEmail = (row) => {
    setEmailTarget(row);
    const num = row.number || "";
    const cust = row.customer || {};
    const label = title.slice(0, -1);
    setEmailForm({
      to: cust.email || "",
      subject: `${label} ${num} — HiCare Lab Solutions`,
      message: `Dear ${cust.name || "Sir/Madam"},\n\nPlease find attached our ${label.toLowerCase()} ${num} dated ${row.date}.\n\nFor any questions, please reply to this email or call us.\n\nRegards,\nHiCare Lab Solutions`,
    });
  };

  const sendEmail = async () => {
    if (!emailForm.to) { toast.error("Recipient email is required"); return; }
    setEmailBusy(true);
    try {
      const html = emailForm.message.replace(/\n/g, "<br/>");
      const { data } = await api.post(`/${resource}/${emailTarget.id}/email`,
        { to: emailForm.to, subject: emailForm.subject, message: html });
      if (data.mocked) toast.info(`Would send to ${data.to} (add SENDGRID_API_KEY in .env to deliver)`);
      else if (data.delivered) toast.success(`Sent to ${data.to}`);
      else toast.error("Send failed — check backend logs");
      setEmailTarget(null);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setEmailBusy(false); }
  };

  const totals = (() => {
    let taxable = 0, tax = 0;
    for (const it of form.items) {
      const t = (Number(it.qty) || 0) * (Number(it.rate) || 0);
      taxable += t; tax += t * (Number(it.gst_rate) || 0) / 100;
    }
    return { taxable, tax, grand: taxable + tax };
  })();

  const save = async () => {
    try { await api.post(`/${resource}`, form); toast.success(`${title} created`); setOpen(false); load();
      setForm({ customer: { name: "", address: "", gstin: "", state: "", state_code: "", phone: "", email: "" }, items: [{ ...EMPTY_ITEM }], is_igst: false, terms: "", notes: "" });
    } catch (e) { toast.error(formatErr(e)); }
  };
  const convert = async (id) => { try { await api.post(`/quotations/${id}/convert-to-invoice`); toast.success("Converted to invoice"); load(); } catch (e) { toast.error(formatErr(e)); } };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/${resource}/${id}`); load(); };
  const openPdf = (id) => { window.open(`${API}/${resource}/${id}/pdf`, "_blank"); };

  const relSent = (le) => {
    if (!le?.sent_at) return null;
    try {
      const d = new Date(le.sent_at);
      const diffMs = Date.now() - d.getTime();
      const mins = Math.round(diffMs / 60000);
      if (mins < 1) return "just now";
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.round(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      const days = Math.round(hrs / 24);
      if (days < 7) return `${days}d ago`;
      return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
    } catch { return null; }
  };

  const sendWhatsApp = async (row) => {
    try {
      const { data } = await api.post(`/${resource}/${row.id}/share`);
      const backendBase = API.replace(/\/api$/, "");
      const url = `${backendBase}${data.path}`;
      const label = title.slice(0, -1);
      const cust = row.customer || {};
      const text = encodeURIComponent(
        `Hello ${cust.name || ""},\n\nPlease find our ${label.toLowerCase()} ${row.number} attached below:\n${url}\n\nRegards,\nHiCare Lab Solutions`
      );
      const phone = (cust.phone || "").replace(/\D/g, "");
      const wa = phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
      window.open(wa, "_blank", "noopener,noreferrer");
      toast.success("WhatsApp opened — pick contact and send");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const updItem = (i, k, v) => setForm((f) => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { ...EMPTY_ITEM }] }));
  const rmItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#0F2942]" style={{ fontFamily: "Outfit, sans-serif" }}>{title}</h1>
          <p className="mt-2 text-sm text-slate-500">{items.length} records</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid={`new-${resource}-btn`} className="bg-[#0F2942] text-white hover:bg-[#0A192F]"><Plus className="mr-1 h-4 w-4" /> New {title.slice(0, -1)}</Button>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Number</TableHead><TableHead>Date</TableHead><TableHead>Customer</TableHead><TableHead>Status</TableHead><TableHead>Last Sent</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {items.map((q) => (
              <TableRow key={q.id} data-testid={`bill-row-${q.id}`}>
                <TableCell className="font-mono text-xs text-[#0F2942]">{q.number}</TableCell>
                <TableCell>{q.date}</TableCell>
                <TableCell>{q.customer?.name}</TableCell>
                <TableCell><span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">{q.status}</span></TableCell>
                <TableCell data-testid={`last-sent-${q.id}`}>
                  {q.last_email?.sent_at ? (
                    <span className="inline-flex items-center gap-1.5 text-xs text-slate-600" title={`To: ${q.last_email.to}${q.last_email.bcc ? ` · BCC: ${q.last_email.bcc}` : ""} · ${new Date(q.last_email.sent_at).toLocaleString("en-IN")}`}>
                      <Mail className={`h-3.5 w-3.5 ${q.last_email.delivered ? "text-emerald-600" : "text-slate-400"}`} />
                      {relSent(q.last_email)}
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => sendWhatsApp(q)} data-testid={`wa-${q.id}`} title="Send via WhatsApp"><MessageCircle className="h-4 w-4 text-emerald-600" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => openEmail(q)} data-testid={`email-${q.id}`} title="Email"><Mail className="h-4 w-4 text-blue-700" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => openPdf(q.id)} data-testid={`pdf-${q.id}`}><Download className="h-4 w-4" /></Button>
                  {resource === "quotations" && q.status !== "converted" && (
                    <Button size="sm" variant="ghost" onClick={() => convert(q.id)} data-testid={`convert-${q.id}`}><FileCheck2 className="h-4 w-4 text-emerald-700" /></Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => remove(q.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-slate-500">No records</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto bg-white">
          <DialogHeader><DialogTitle className="text-xl font-bold text-[#0F2942]">New {title.slice(0, -1)}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Customer</div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input data-testid="cust-name" value={form.customer.name} onChange={(e) => setForm({ ...form, customer: { ...form.customer, name: e.target.value } })} /></div>
                <div><Label>GSTIN</Label><Input value={form.customer.gstin} onChange={(e) => setForm({ ...form, customer: { ...form.customer, gstin: e.target.value } })} /></div>
                <div className="col-span-2"><Label>Address</Label><Textarea rows={2} value={form.customer.address} onChange={(e) => setForm({ ...form, customer: { ...form.customer, address: e.target.value } })} /></div>
                <div><Label>State</Label><Input value={form.customer.state} onChange={(e) => setForm({ ...form, customer: { ...form.customer, state: e.target.value } })} /></div>
                <div><Label>State Code</Label><Input value={form.customer.state_code} onChange={(e) => setForm({ ...form, customer: { ...form.customer, state_code: e.target.value } })} /></div>
                <div><Label>Phone</Label><Input value={form.customer.phone} onChange={(e) => setForm({ ...form, customer: { ...form.customer, phone: e.target.value } })} /></div>
                <div><Label>Email</Label><Input value={form.customer.email} onChange={(e) => setForm({ ...form, customer: { ...form.customer, email: e.target.value } })} /></div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Switch id="igst" checked={form.is_igst} onCheckedChange={(v) => setForm({ ...form, is_igst: v })} />
                <Label htmlFor="igst">Inter-state (IGST) — otherwise CGST + SGST</Label>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 p-4">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Line Items</div>
                <Button size="sm" variant="outline" onClick={addItem} data-testid="add-line"><Plus className="mr-1 h-3.5 w-3.5" /> Row</Button>
              </div>
              <div className="mt-3 space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2">
                    <Input className="col-span-4" placeholder="Description" data-testid={`line-desc-${i}`} value={it.description} onChange={(e) => updItem(i, "description", e.target.value)} />
                    <Input className="col-span-1" placeholder="HSN" value={it.hsn} onChange={(e) => updItem(i, "hsn", e.target.value)} />
                    <Input className="col-span-1" type="number" placeholder="Qty" value={it.qty} onChange={(e) => updItem(i, "qty", Number(e.target.value))} />
                    <Input className="col-span-1" placeholder="Unit" value={it.unit} onChange={(e) => updItem(i, "unit", e.target.value)} />
                    <Input className="col-span-2" type="number" placeholder="Rate" value={it.rate} onChange={(e) => updItem(i, "rate", Number(e.target.value))} />
                    <Input className="col-span-2" type="number" placeholder="GST%" value={it.gst_rate} onChange={(e) => updItem(i, "gst_rate", Number(e.target.value))} />
                    <Button variant="ghost" size="sm" onClick={() => rmItem(i)} className="col-span-1"><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex justify-end gap-8 text-sm">
                <div>Taxable: <b>₹ {totals.taxable.toFixed(2)}</b></div>
                <div>GST: <b>₹ {totals.tax.toFixed(2)}</b></div>
                <div>Grand: <b className="text-[#0F2942]">₹ {totals.grand.toFixed(2)}</b></div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Terms</Label><Textarea rows={2} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} placeholder="Payment: 100% advance. Delivery: 2-3 weeks." /></div>
              <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>

            <Button onClick={save} data-testid="save-bill-btn" className="w-full bg-[#0F2942] text-white hover:bg-[#0A192F]">Save {title.slice(0, -1)}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!emailTarget} onOpenChange={(o) => !o && setEmailTarget(null)}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader><DialogTitle className="text-xl font-bold text-[#0F2942]">Email {title.slice(0, -1)} {emailTarget?.number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>To</Label><Input type="email" data-testid="email-to" value={emailForm.to} onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })} /></div>
            <div><Label>Subject</Label><Input data-testid="email-subject" value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} /></div>
            <div><Label>Message</Label><Textarea rows={6} data-testid="email-message" value={emailForm.message} onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })} /></div>
            <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">📎 The GST PDF will be attached automatically.</div>
            <Button onClick={sendEmail} disabled={emailBusy} data-testid="email-send-btn" className="w-full bg-[#0F2942] text-white hover:bg-[#0A192F]">
              {emailBusy ? "Sending…" : "Send Email"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
